import React from "react"
import { graphql } from "gatsby"
import { Box, Container, Typography } from "@mui/material"
import Seo from "../components/Seo"
import YoutubeEmbed from "../components/YoutubeEmbed"
import { BLOCKS, MARKS } from "@contentful/rich-text-types"
import { documentToReactComponents } from "@contentful/rich-text-react-renderer"
import { GatsbyImage } from "gatsby-plugin-image"
import { useLocation } from "@reach/router"
import ButtonStyled from "../components/ButtonStyled"
import HeroImage from "../components/HeroImage/HeroImage"
import VideoBlock from "../components/VideoBlock"
import BackgroundWrapper from "../components/BackgroundWrapper"

const getOptions = (references) => ({
  renderMark: {
    [MARKS.BOLD]: (text) => <span style={{ fontWeight: 500 }}>{text}</span>,
  },
  renderNode: {
    [BLOCKS.HEADING_5]: (node, children) => (
      <Typography
        component="h2"
        variant="h6"
        sx={{
          marginTop: 2,
        }}
      >
        {children}
      </Typography>
    ),
    [BLOCKS.PARAGRAPH]: (node, children) => (
      <Typography
        variant="body1"
        sx={{
          marginTop: 2,
        }}
      >
        {children}
      </Typography>
    ),
    [BLOCKS.EMBEDDED_ENTRY]: (node) => {
      const entryId = node.data.target.sys.id
      const entryRef = references.filter(
        ({ contentful_id }) => contentful_id === entryId
      )[0]
      const isVideo =
        entryRef && entryRef.sys.contentType.sys.id === "videoEmbed"
      if (isVideo) {
        return <YoutubeEmbed {...entryRef} />
      }
      return <Typography>Embed error</Typography>
    },
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const imageId = node.data.target.sys.id
      const image = references.filter(
        ({ contentful_id }) => contentful_id === imageId
      )[0]
      const { imageData, title, description } = {
        imageData: image ? image.gatsbyImageData : null,
        title: image ? image.title : "",
        description: image ? image.description : "",
      }
      return (
        <GatsbyImage
          image={imageData}
          alt={`${title}${description ? ` - ${description}` : ""}`}
        />
      )
    },
  },
})

export default function Page({ data }) {
  const { title, content, buttonText, buttonLink, heroImage, video } =
    data.contentfulPage

  const location = useLocation()

  return (
    <Box>
      {heroImage && <HeroImage image={heroImage.gatsbyImageData} alt={title} />}

      <Seo
        title={title}
        pathname={location.pathname}
        article={null}
        image={heroImage ? heroImage.gatsbyImageData.images.fallback.src : null}
      />
      <BackgroundWrapper
        backgroundColor="background.light"
        bottomGutterSpacing={8}
        topGutterSpacing={8}
      >
        <Container maxWidth="md" sx={{ minHeight: "25vh" }}>
          <Typography
            component="h1"
            variant={"h2"}
            sx={{
              marginBottom: 2,
              fontSize: `30px`,
              textTransform: `uppercase`,
              color: `titles.main`,
            }}
          >
            {title}
          </Typography>
          {documentToReactComponents(
            JSON.parse(content.raw),
            getOptions(content.references)
          )}
        </Container>
        {buttonText && (
          <Box
            sx={{
              textAlign: "center",
              marginTop: 6,
              marginBottom: 6,
            }}
          >
            <ButtonStyled text={buttonText} route={buttonLink} external />
          </Box>
        )}
      </BackgroundWrapper>
      {video && <VideoBlock src={video.file.url} />}
    </Box>
  )
}

export const pageQuery = graphql`
  query ($slug: String!) {
    contentfulPage(slug: { eq: $slug }) {
      id
      title
      slug
      buttonLink
      buttonText
      heroImage {
        gatsbyImageData(placeholder: BLURRED, formats: AUTO)
      }
      video {
        file {
          url
        }
      }
      content {
        raw
        references {
          ... on ContentfulAsset {
            contentful_id
            title
            description
            __typename
            gatsbyImageData(
              width: 600
              placeholder: TRACED_SVG
              formats: [AUTO, WEBP]
            )
          }
        }
      }
    }
  }
`
